import { Awareness } from '@/components'
import { EditorPill } from './EditorPill'
import { LinkPillReadOnly } from './LinkPillReadOnly'
import { PillNames } from './PillNames'
import { linkTitles } from './linkTitles'
import { ComboBox } from '@ttab/elephant-ui'
import type { LucideIcon } from '@ttab/elephant-ui/icons'
import type { YDocument } from '@/modules/yjs/hooks'
import { useYValue } from '@/modules/yjs/hooks'
import { Block } from '@ttab/elephant-api/newsdoc'
import { useRef, type JSX } from 'react'
import type * as Y from 'yjs'
import { useTranslation } from 'react-i18next'

export type LinkPillOption = {
  value: string
  label: string
}

/**
 * A link list field on the editor surface itself, above the text, so it is part
 * of what you look at while writing and it is obvious when it is empty. Nothing
 * is ever prefilled, setting it is always a deliberate act by the author.
 *
 * The field itself - which link type, which options, which words - is the
 * caller's, so byline and content source are the same pill with a different
 * configuration rather than two copies of it.
 *
 * A lead, when given, is what gets published instead of the names, and counts
 * as a value. The action is the caller's own control at the end of the pill.
 */
export const LinkPill = ({
  ydoc,
  readOnly,
  field,
  path,
  blockType,
  rel,
  idKey,
  options,
  Icon,
  addLabel,
  emptyLabel,
  lead,
  hint,
  action
}: {
  ydoc: YDocument<Y.Map<unknown>>
  readOnly?: boolean
  field: string
  path: string
  blockType: string
  rel: string
  idKey: 'uuid' | 'uri'
  options: LinkPillOption[]
  Icon: LucideIcon
  addLabel: string
  emptyLabel: string
  lead?: string
  hint?: string
  action?: JSX.Element
}): JSX.Element => {
  const { t } = useTranslation('metaSheet')
  const [links, setLinks] = useYValue<Block[] | undefined>(ydoc.ele, path)
  const setFocused = useRef<(value: boolean, path: string) => void>(() => { })
  const hasValue = !!links?.length || !!lead

  if (readOnly) {
    return (
      <LinkPillReadOnly
        field={field}
        links={links}
        Icon={Icon}
        emptyLabel={emptyLabel}
        lead={lead}
        hint={hint}
      />
    )
  }

  const selectedOptions = options.filter((option) =>
    links?.some((link) => link[idKey] === option.value))

  return (
    <EditorPill field={field} hasValue={hasValue}>
      <>
        <Awareness ref={setFocused} ydoc={ydoc} path={path}>
          <ComboBox
            size='xs'
            variant='ghost'
            modal={true}
            sortOrder='label'
            options={options}
            selectedOptions={selectedOptions}
            onOpenChange={(isOpen: boolean) => {
              setFocused.current(true, (isOpen) ? path : '')
            }}
            onSelect={(option) => {
              if ((links || []).some((link) => link[idKey] === option.value)) {
                setLinks(links?.filter((link: Block) => {
                  return link[idKey] !== option.value
                }))
              } else {
                setLinks([...(links || []), Block.create({
                  type: blockType,
                  rel,
                  [idKey]: option.value,
                  title: option.label
                })])
              }
            }}
            translationStrings={{
              nothingFound: t('common:misc.nothingFound'),
              searching: t('common:misc.searching')
            }}
          >
            {hasValue
              ? <Icon size={20} strokeWidth={1.75} />
              : (
                  <>
                    <Icon size={20} strokeWidth={1.75} />
                    <span className='text-sm text-muted-foreground pl-2'>
                      {addLabel}
                    </span>
                  </>
                )}
          </ComboBox>
        </Awareness>

        {hasValue && (
          <PillNames names={linkTitles(links)} placeholder={addLabel} lead={lead} hint={hint} />
        )}

        {action}
      </>
    </EditorPill>
  )
}
